import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bookmarksApi } from '@/lib/api';
import type { Bookmark, BookmarkWithTags } from '@/types/database';

export const bookmarkKeys = {
  all: ['bookmarks'] as const,
  lists: () => [...bookmarkKeys.all, 'list'] as const,
  list: (filters: { collection_id?: string; search?: string; archived?: boolean }) =>
    [...bookmarkKeys.lists(), filters] as const,
  details: () => [...bookmarkKeys.all, 'detail'] as const,
  detail: (id: string) => [...bookmarkKeys.details(), id] as const,
};

interface BookmarkFilters {
  collection_id?: string;
  search?: string;
  archived?: boolean;
}

export function useBookmarks(filters: BookmarkFilters = {}) {
  return useQuery({
    queryKey: bookmarkKeys.list(filters),
    queryFn: async () => {
      const data = await bookmarksApi.getAll({
        collection_id: filters.collection_id,
        search: filters.search,
        archived: filters.archived,
      });

      // Transform to match expected format
      const bookmarksWithTags: BookmarkWithTags[] = data.map((bookmark: Bookmark & { tags?: string[] }) => ({
        ...bookmark,
        tags: (bookmark.tags || []).map((tag: string) => ({ name: tag, id: '', user_id: '', color: null, created_at: '' })),
      }));

      return bookmarksWithTags;
    },
  });
}

export function useCreateBookmark() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bookmark: Partial<Bookmark>) => bookmarksApi.create(bookmark),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookmarkKeys.all });
    },
  });
}

export function useUpdateBookmark() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Bookmark> }) =>
      bookmarksApi.update(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookmarkKeys.all });
    },
  });
}

export function useDeleteBookmark() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => bookmarksApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookmarkKeys.all });
    },
  });
}
